// Number/date formatting + category metadata shared across pages.
// Money values arrive from the API as plain decimal strings ("4111706.35");
// they are displayed in Indian digit grouping ("41,11,706.35").

// Category strings from the GENERATED vocabulary (contract separation,
// Stage 2): the backend's taxonomy is the one source, `dump_openapi.py`
// writes vocabulary.json, and the gate's contract step fails on drift.
import vocab from './api/vocabulary.json'

const inr = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const int = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })

export function fmtMoney(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? inr.format(n) : String(v)
}

export function fmtInt(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : int.format(v)
}

export function fmtBytes(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / 1024 / 1024).toFixed(1)} MB`
}

/** The zone every timestamp on these screens is stated in.
 *
 *  Fixed, not the viewer's. These are audit surfaces — the preparer, the
 *  reviewer and the partner signing the resolution register have to read one
 *  timestamp and mean the same moment, and a value that silently changes
 *  meaning with whoever opened the page is worse than a slightly foreign one.
 *  IST has no daylight saving, so the offset is a constant +05:30.
 */
const IST = 'Asia/Kolkata'

const stamp = new Intl.DateTimeFormat('en-IN', {
  timeZone: IST, day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
})

/** An INSTANT, rendered in IST and labelled.
 *
 *  This used to be `new Date(iso).toLocaleString()` against an API that sent no
 *  designator, and per the ECMAScript spec a date-time string without one is
 *  parsed as LOCAL time. So 14:46 UTC printed as 14:46 — five and a half hours
 *  wrong, on the line that says when the workbook going to the client was
 *  generated.
 *
 *  Use `fmtDate` for a business date. Only a moment belongs here.
 */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  // A string with no designator predates the backend sending one, and it was
  // written by `now()`, which has always been UTC. Reading it as local would be
  // the original bug all over again, quietly, on older rows.
  const text = /(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`
  const d = new Date(text)
  if (Number.isNaN(d.getTime())) return String(iso)
  const parts = Object.fromEntries(
    stamp.formatToParts(d).map((p) => [p.type, p.value]),
  )
  return `${Number(parts.day)} ${parts.month} ${parts.year}, `
    + `${parts.hour}:${parts.minute} IST`
}

/** A BUSINESS DATE — the day something happened, not a moment.
 *
 *  Never constructs a Date. `new Date('2026-04-01')` parses as UTC midnight, and
 *  in any negative-offset zone that renders as 31 March: the first day of the
 *  Indian financial year silently becomes the last day of the previous one, and
 *  a Q4 deduction lands in Q3 of the wrong TDS return. Reading the day out of
 *  the string cannot do that.
 */
export function fmtDate(iso: string | null | undefined,
                       opts: { year?: boolean } = {}): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return String(iso)
  const day = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`
  return opts.year === false ? day : `${day} ${m[1]}`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2025-04" -> "Apr-25" */
export function fmtMonthKey(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key)
  if (!m) return key
  return `${MONTHS[Number(m[2]) - 1]}-${m[1].slice(2)}`
}

export function shortSha(sha: string, n = 12): string {
  return sha.slice(0, n)
}

// The presentation (short labels, colors) stays here — it is the frontend's.
const CAT = vocab.categories
export const CATEGORY_META: Record<string, { short: string; color: string }> = {
  [CAT.agreed]: { short: 'Agreed', color: 'green' },
  [CAT.with_difference]: { short: 'Difference', color: 'yellow' },
  [CAT.only_26as]: { short: '26AS only', color: 'red' },
  [CAT.only_books]: { short: 'Ledger only', color: 'orange' },
  [CAT.nil]: { short: 'NIL', color: 'gray' },
}

// the engine's own emission order, stated by the vocabulary rather than by
// the incidental key order of the map above
export const CATEGORIES: string[] = vocab.category_order

export function categoryMeta(category: string): { short: string; color: string } {
  return CATEGORY_META[category] ?? { short: category, color: 'blue' }
}

/** A run's short human handle: the first 8 hex of its UUID — the SAME tag the
 *  server prints into export filenames, so the run a person sees on screen is
 *  the run their download names. The predecessor showed `#43`; there is no
 *  integer any more, and a whole UUID is a label nobody can read. */
export function runTag(id: string | null | undefined): string {
  if (!id) return '—'
  return id.replace(/-/g, '').slice(0, 8)
}
